import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorOptions,
	CreatorProduct,
	CreatorState,
	SelectedAttributeValue,
} from '@/types/creator';
import { Product, ProductStatus } from '@/api/ProductApi';
import { Component } from '@/api/ComponentApi';
import { UUID } from '@/types/types';
import {
	getInitialSelectedComponents,
	getInitialSelectedAttributes,
	getInitialAvailableAttributeValues,
} from '@/utils/utilities';
import { ProductType } from '@/api/ProductTypeApi';

const url = new URL(window.location.href);
const searchParams = new URLSearchParams(url.search);

const productTypeId = searchParams.get('productTypeId') as UUID;
const productId = searchParams.get('productId') as UUID | null;

const initialState: CreatorState = {
	product: {
		id: productId || crypto.randomUUID(),
		netPrice: 0,
		grossPrice: 0,
		productType: null,
		status: ProductStatus.NOT_ADDED,
		updatedAt: '',
		createdAt: '',
		productComponents: [],
		width: 50,
		height: 100,
		quantity: 1,
	},
	options: {
		productId,
		syncProjectCounter: 0,
		isInitialized: false,
		productTypeId,
	},
	selectedComponents: {},
	selectedAttributeValues: {},
	components: {},
	attributeValuesWithAvailability: {},
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		syncFromApi: (state, { payload }: PayloadAction<Product>) => {
			Object.assign(state.product, {
				id: payload.id,
				createdAt: payload.createdAt,
				updatedAt: payload.updatedAt,
				netPrice: payload.netPrice,
				grossPrice: payload.grossPrice,
				productComponents: payload.productComponents,
			});
		},
		setProductType: (state, { payload }: PayloadAction<ProductType>) => {
			state.product.productType = payload;

			const selectedComponents = getInitialSelectedComponents(
				payload.componentCategories
			);
			const selectedAttributes = getInitialSelectedAttributes(
				payload.componentCategories
			);

			state.selectedComponents = selectedComponents;
			state.selectedAttributeValues = selectedAttributes;
		},
		loadProduct: (state, { payload }: PayloadAction<Product>) => {
			state.options.isInitialized = true;

			state.product.createdAt = payload.createdAt;
			state.product.updatedAt = payload.updatedAt;
			state.product.netPrice = payload.netPrice;
			state.product.grossPrice = payload.grossPrice;
			state.product.productComponents = payload.productComponents;
			state.product.quantity = payload.quantity;
			state.product.width = payload.width;
			state.product.height = payload.height;

			state.selectedAttributeValues = payload.options.selectedAttributes;
		},
		setComponents: (
			state,
			{ payload }: PayloadAction<CreatorState['components']>
		) => {
			state.components = payload;

			const attributeValuesWithAvailability =
				getInitialAvailableAttributeValues(payload);
			state.attributeValuesWithAvailability = attributeValuesWithAvailability;
		},
		updateDimensions: (
			state,
			{ payload }: PayloadAction<{ key: 'width' | 'height'; value: number }>
		) => {
			state.product[payload.key] = payload.value;
		},
		updateProduct: (
			state,
			{ payload }: PayloadAction<Partial<CreatorProduct>>
		) => {
			Object.assign(state.product, payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		refreshAPIData: (state) => {
			state.options.syncProjectCounter++;
		},
		resetCreator: (state) => {
			Object.assign(state.product, {
				netPrice: 0,
				grossPrice: 0,
				productComponents: [],
			});

			const selectedComponents = getInitialSelectedComponents(
				state.product.productType!.componentCategories
			);
			const selectedAttributes = getInitialSelectedAttributes(
				state.product.productType!.componentCategories
			);

			Object.assign(state.options, { selectedComponents, selectedAttributes });
		},
		changeSelectedComponent: (
			state,
			{
				payload,
			}: PayloadAction<{
				componentCategoryId: UUID;
				component: Component | null;
			}>
		) => {
			state.selectedComponents[payload.componentCategoryId] = payload.component;
		},
		changeSelectedAttributes: (
			state,
			{
				payload,
			}: PayloadAction<{
				componentCategoryId: UUID;
				attributeId: UUID;
				attributeValueId: SelectedAttributeValue;
			}>
		) => {
			state.selectedAttributeValues[payload.componentCategoryId][
				payload.attributeId
			] = payload.attributeValueId;

			const attributes =
					state.attributeValuesWithAvailability[payload.componentCategoryId],
				components = state.components[payload.componentCategoryId];

			for (const [attributeId, attributeValues] of Object.entries(attributes)) {
				if (attributeId === payload.attributeId) continue;

				const selectedValuesForCategory = Object.entries(
					state.selectedAttributeValues[payload.componentCategoryId]
				)
					.filter(([attrId]) => attrId !== attributeId)
					.map((entry) => entry[1]);

				for (const attributeValue of attributeValues) {
					const mergedValues = [
						...selectedValuesForCategory,
						attributeValue.id,
					];

					const componentMatch = components.find((component) =>
						component.attributeValues.every((attrValue) =>
							mergedValues.includes(attrValue.id)
						)
					);

					attributeValue.isAvailable = true;
				}
			}
		},
		setSelectedValuesFromComponent: (
			state,
			{
				payload,
			}: PayloadAction<{
				componentCategoryId: UUID;
				component: Component;
			}>
		) => {
			state.selectedAttributeValues[payload.componentCategoryId] =
				payload.component.attributeValues.reduce((acc, attributeValue) => {
					acc[attributeValue.attribute.id] = attributeValue.id;

					return acc;
				}, {} as Record<string, SelectedAttributeValue>);
		},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
